/* =====================================================================
 * FilePreviewPanel — 파일 미리보기 패널 (좌측 50%)
 * ===================================================================== */

import React, { useEffect, useRef, useState, useCallback, Suspense, lazy } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  Check,
  ChevronDown,
  X,
  Maximize2,
  Shrink,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  AlertTriangle,
  Eye,
  Code2,
} from 'lucide-react';
import { Tooltip } from '@auw/workspace-ui';
import { FilePreviewSkeleton } from '@auw/workspace-ui';
import {
  isExcelFile, isDocxFile, isPptxFile, isMarkdownFile, isImageFile, isVideoFile, isAudioFile, isCodeFile, isHtmlFile,
} from './utils';
import { useChatStore } from '@auw/stores';
import { getFileServeUrl } from '@auw/api-client';
import { authFetch } from '@auw/api-client';
import type { FileAttachment, WorkspaceFile } from '@auw/workspace-utils';
import { resolveDocxPreviewRendererComponent } from '../../lib/docxPreviewRendererSlot';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM_INDEX = 2; // 100%
const RENDER_NOTICE_LABEL = '현재 미리보기는 실제 파일과 다르게 보일 수 있으니 참고용으로만 사용해 주세요.';

const PdfRenderer = lazy(() =>
  import('./renderers/PdfRenderer').then((m) => ({ default: m.PdfRenderer })),
);
const ExcelRenderer = lazy(() =>
  import('./renderers/ExcelRenderer').then((m) => ({ default: m.ExcelRenderer })),
);
const BuiltInDocxRenderer = lazy(() =>
  import('./renderers/DocxRenderer').then((m) => ({ default: m.DocxRenderer })),
);
const PptxRenderer = lazy(() =>
  import('./renderers/PptxRenderer').then((m) => ({ default: m.PptxRenderer })),
);
const MarkdownFileRenderer = lazy(() =>
  import('./renderers/MarkdownFileRenderer').then((m) => ({ default: m.MarkdownFileRenderer })),
);
const ImageRenderer = lazy(() =>
  import('./renderers/ImageRenderer').then((m) => ({ default: m.ImageRenderer })),
);
const VideoRenderer = lazy(() =>
  import('./renderers/VideoRenderer').then((m) => ({ default: m.VideoRenderer })),
);
const CodeFileRenderer = lazy(() =>
  import('./renderers/CodeFileRenderer').then((m) => ({ default: m.CodeFileRenderer })),
);
const HtmlFileRenderer = lazy(() =>
  import('./renderers/HtmlFileRenderer').then((m) => ({ default: m.HtmlFileRenderer })),
);

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Panel = styled.div<{ $fullscreen?: boolean }>`
  width: 60%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgSecondary};
  flex-shrink: 0;
  animation: ${fadeIn} 0.25s ease;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 10px 0 16px 10px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 4px 16px rgba(0, 0, 0, 0.06),
    0 12px 40px rgba(0, 0, 0, 0.04);

  /* WorkspaceSplitLayout 내부에서 사용될 때 */
  .preview-embedded & {
    width: auto;
    flex: 1;
    flex-shrink: 1;
    margin: 0;
  }

  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      width: 100%;
      margin: 10px 10px 16px 10px;
      border-radius: 16px;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
    margin: 8px;
    border-radius: 14px;
  }
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  min-height: 48px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.bgPrimary};
  gap: 8px;
  border-radius: 16px 16px 0 0;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const FileNameTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  padding: 4px 6px;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const FileNameText = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const RenderNoticeButton = styled.button`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 999px;
  border: 1px solid
    ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(15, 23, 42, 0.12)')};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(248, 250, 252, 0.12)' : 'rgba(15, 23, 42, 0.06)')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#334155')};
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(248, 250, 252, 0.18)' : 'rgba(15, 23, 42, 0.1)')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.22)' : 'rgba(15, 23, 42, 0.18)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px
      ${({ theme }) => (theme.mode === 'dark' ? 'rgba(248, 250, 252, 0.2)' : 'rgba(15, 23, 42, 0.12)')};
  }
`;

const FileNameChevron = styled.span<{ $open?: boolean }>`
  flex-shrink: 0;
  display: flex;
  color: ${({ theme }) => theme.colors.textMuted};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0')});
  transition: transform 0.15s ease;
`;

const FileSelectorPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 10px;
  right: 10px;
  margin-top: 2px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
`;

const FileSelectorItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active ? theme.colors.bgHover : 'transparent')};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const FileSelectorName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSelectorChip = styled.span<{ $type: 'upload' | 'generated' }>`
  flex-shrink: 0;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1.2;
  ${({ $type, theme }) =>
    $type === 'upload'
      ? css`
          background: ${theme.mode === 'dark' ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)'};
          color: ${theme.mode === 'dark' ? '#93bbfd' : '#3b82f6'};
        `
      : css`
          background: ${theme.mode === 'dark' ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)'};
          color: ${theme.mode === 'dark' ? '#86efac' : '#16a34a'};
        `}
`;

const FileSelectorCheck = styled.span`
  flex-shrink: 0;
  display: flex;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const ControlBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ZoomLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 40px;
  text-align: center;
  user-select: none;
`;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 4px;
`;

const Content = styled.div<{ $noScroll?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $noScroll }) => ($noScroll ? 'hidden' : 'auto')};
  position: relative;
  border-radius: 0 0 16px 16px;

  /* 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollbarHover};
  }
`;

const SkeletonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 0 0 16px 16px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  overflow: hidden;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`;

const ErrorText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.error};
`;

const UnsupportedText = styled.span`
  font-size: 0.875rem;
`;

const AudioPreviewWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface FilePreviewPanelProps {
  file: FileAttachment;
  onClose?: () => void;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  headerRight?: React.ReactNode;
  sessionFilesOverride?: readonly WorkspaceFile[];
  onSelectFileOverride?: (file: WorkspaceFile) => void;
}

function wfToAttachment(wf: WorkspaceFile): FileAttachment {
  const serveUrl = getFileServeUrl(wf.file_id);
  return {
    id: wf.file_id,
    name: wf.original_name,
    type: wf.mime_type,
    size: wf.size_bytes,
    previewUrl: serveUrl,
    file: new File([], wf.original_name),
    remoteUrl: serveUrl,
  };
}

export const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({ file, onClose, fullscreen, onToggleFullscreen, headerRight, sessionFilesOverride, onSelectFileOverride }) => {
  // Enterprise는 문서에이전트 엔진을 bootstrap에서 주입한다. 다른 호스트는
  // 기존 lightweight 구현을 그대로 사용한다.
  const DocxRenderer = resolveDocxPreviewRendererComponent(BuiltInDocxRenderer);
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM_INDEX);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  // HTML 전용: false = 코드 보기(기본, 기존 동작), true = 렌더링 미리보기(iframe).
  const [htmlPreview, setHtmlPreview] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const storeSessionFiles = useChatStore((s) => s.sessionFiles);
  const sessionFiles = sessionFilesOverride ?? storeSessionFiles;
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const addToast = useChatStore((s) => s.addToast);
  const hasSessionFiles = sessionFiles.length > 1;
  const isVideo = isVideoFile(file.name);
  const isAudio = isAudioFile(file.name);

  /* 원격 파일(remoteUrl) → 실제 File blob 변환 */
  const [resolvedFile, setResolvedFile] = useState<File | null>(null);
  const isRemote = !!file.remoteUrl && (file.file?.size ?? 0) === 0;
  const shouldResolveRemoteFile = isRemote;

  useEffect(() => {
    setLoading(true);
    setError(null);
    // 파일이 바뀌면 HTML 미리보기 토글은 기본(코드 보기)으로 초기화
    setHtmlPreview(false);
    setZoomIdx(DEFAULT_ZOOM_INDEX);
  }, [file.id]);

  useEffect(() => {
    if (!shouldResolveRemoteFile) {
      setResolvedFile(null);
      return;
    }
    let cancelled = false;
    setResolvedFile(null);
    setLoading(true);
    setError(null);

    authFetch(file.remoteUrl!)
      .then((res) => {
        if (res.status === 413) throw new Error('FILE_TOO_LARGE');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        setResolvedFile(new File([blob], file.name, { type: file.type || blob.type }));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[FilePreviewPanel] Remote fetch failed:', err);
        setError(
          err.message === 'FILE_TOO_LARGE'
            ? '파일 용량이 200MB를 초과하여 불러올 수 없습니다.'
            : '파일을 서버에서 불러올 수 없습니다.',
        );
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [file.remoteUrl, file.name, file.type, shouldResolveRemoteFile]);

  const effectiveFile = shouldResolveRemoteFile ? resolvedFile : file.file;

  const handleSelectFile = useCallback((wf: WorkspaceFile) => {
    if (onSelectFileOverride) onSelectFileOverride(wf);
    else setPreviewFile(wfToAttachment(wf));
    setSelectorOpen(false);
  }, [onSelectFileOverride, setPreviewFile]);

  useEffect(() => {
    if (!selectorOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        selectorRef.current && !selectorRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setSelectorOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectorOpen]);

  const scale = ZOOM_LEVELS[zoomIdx];
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const isExcel = isExcelFile(file.name);
  const isDocx = isDocxFile(file.name);
  const isPptx = isPptxFile(file.name);
  const isMarkdown = isMarkdownFile(file.name);
  const isImage = isImageFile(file.name);
  const isCode = isCodeFile(file.name);
  const isHtml = isHtmlFile(file.name);
  const showRenderNotice = isExcel || isDocx || isPptx;

  const handleZoomIn = useCallback(() => {
    setZoomIdx((prev) => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomIdx((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomIdx(DEFAULT_ZOOM_INDEX);
  }, []);

  const handleLoadComplete = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  const handleLoadStart = useCallback(() => {
    setLoading(true);
    setError(null);
  }, []);

  const handleError = useCallback((msg: string) => {
    setLoading(false);
    setError(msg);
  }, []);

  useEffect(() => {
    if (isRemote) return;
    if (!file.file) {
      setLoading(false);
      setError('파일 데이터가 없어 미리보기를 불러올 수 없습니다.');
      return;
    }
    setError(null);
  }, [isRemote, file.file]);

  const isSupportedRenderer =
    isExcel || isDocx || isPptx || isMarkdown || isImage || isVideo || isAudio || isCode || ext === 'pdf';

  useEffect(() => {
    if (!effectiveFile) return;
    if (!isSupportedRenderer) {
      setLoading(false);
      setError(null);
    }
  }, [effectiveFile, isSupportedRenderer]);

  /* 확장자별 렌더러 분기 */
  const renderContent = () => {
    if (!effectiveFile) return null;

    // Blob URL/remote fetch 교체까지 렌더러 수명에 반영하되, 동일 파일의 단순
    // 부모 리렌더에서는 세션을 유지한다.
    const fileIdentity =
      file.id || file.remoteUrl || file.previewUrl || `${file.name}_${file.size}`;
    const fKey = `${fileIdentity}_${effectiveFile.name}_${effectiveFile.size}_${effectiveFile.lastModified}`;

    if (isExcel) {
      return (
        <ExcelRenderer
          key={fKey}
          file={effectiveFile}
          sessionMode="owned"
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
          onToast={addToast}
        />
      );
    }

    if (isDocx) {
      return (
        <DocxRenderer
          key={fKey}
          file={effectiveFile}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isPptx) {
      return (
        <PptxRenderer
          key={fKey}
          file={effectiveFile}
          editable={false}
          showToolbar={false}
          downloadMode="local"
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isMarkdown) {
      return (
        <MarkdownFileRenderer
          key={fKey}
          file={effectiveFile}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isImage) {
      return (
        <ImageRenderer
          key={fKey}
          file={effectiveFile}
          scale={scale}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isVideo) {
      return (
        <VideoRenderer
          key={fKey}
          file={effectiveFile}
          remoteUrl={file.remoteUrl}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isAudio) {
      const audioUrl = URL.createObjectURL(effectiveFile);
      return (
        <AudioPreviewWrap key={fKey}>
          <audio
            controls
            preload="metadata"
            src={audioUrl}
            onLoadedMetadata={handleLoadComplete}
            onError={() => handleError('오디오 파일을 재생할 수 없습니다.')}
            style={{ width: '100%', maxWidth: 480 }}
          />
        </AudioPreviewWrap>
      );
    }

    if (isHtml && htmlPreview) {
      return (
        <HtmlFileRenderer
          key={fKey + '_preview'}
          file={effectiveFile}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    if (isCode) {
      return (
        <CodeFileRenderer
          key={fKey}
          file={effectiveFile}
          fileName={file.name}
          onLoadComplete={handleLoadComplete}
          onLoadStart={handleLoadStart}
          onError={handleError}
        />
      );
    }

    switch (ext) {
      case 'pdf':
        return (
          <PdfRenderer
            file={effectiveFile}
            scale={scale}
            onLoadComplete={handleLoadComplete}
            onLoadStart={handleLoadStart}
            onError={handleError}
          />
        );
      default:
        return (
          <ErrorContainer>
            <UnsupportedText>
              .{ext} 파일은 아직 미리보기를 지원하지 않습니다.
            </UnsupportedText>
          </ErrorContainer>
        );
    }
  };

  return (
    <Panel $fullscreen={fullscreen}>
      <Header data-file-preview-header>
        {hasSessionFiles ? (
          <TitleGroup>
            <FileNameTrigger
              ref={triggerRef}
              type="button"
              onClick={() => setSelectorOpen((v) => !v)}
              title={file.name}
            >
              <FileNameText>{file.name}</FileNameText>
              <FileNameChevron $open={selectorOpen}>
                <ChevronDown size={14} />
              </FileNameChevron>
            </FileNameTrigger>
            {showRenderNotice && (
              <Tooltip label={RENDER_NOTICE_LABEL} position="bottom" align="start">
                <RenderNoticeButton type="button" aria-label="렌더링 안내">
                  !
                </RenderNoticeButton>
              </Tooltip>
            )}
            {selectorOpen && (
              <FileSelectorPanel ref={selectorRef}>
                {sessionFiles.map((wf) => {
                  const isActive = wf.file_id === file.id;
                  const isUpload = wf.source_type?.includes('upload');
                  return (
                    <FileSelectorItem
                      key={wf.file_id}
                      $active={isActive}
                      onClick={() => handleSelectFile(wf)}
                    >
                      <FileSelectorName title={wf.original_name}>{wf.original_name}</FileSelectorName>
                      <FileSelectorChip $type={isUpload ? 'upload' : 'generated'}>
                        {isUpload ? '업로드' : '생성'}
                      </FileSelectorChip>
                      {isActive && (
                        <FileSelectorCheck><Check size={14} /></FileSelectorCheck>
                      )}
                    </FileSelectorItem>
                  );
                })}
              </FileSelectorPanel>
            )}
          </TitleGroup>
        ) : (
          <TitleGroup>
            <FileNameText title={file.name} style={{ flex: 1, minWidth: 0, padding: '4px 6px' }}>
              {file.name}
            </FileNameText>
            {showRenderNotice && (
              <Tooltip label={RENDER_NOTICE_LABEL} position="bottom" align="start">
                <RenderNoticeButton type="button" aria-label="렌더링 안내">
                  !
                </RenderNoticeButton>
              </Tooltip>
            )}
          </TitleGroup>
        )}
        <Controls>
          {!isExcel && !isDocx && !isPptx && !isMarkdown && !isVideo && !isCode && (
            <>
              <Tooltip label="축소">
                <ControlBtn aria-label="축소" onClick={handleZoomOut} disabled={zoomIdx === 0}>
                  <ZoomOut size={16} />
                </ControlBtn>
              </Tooltip>
              <ZoomLabel title={ext === 'pdf' ? '페이지 맞춤 기준 배율' : undefined}>
                {ext === 'pdf' && scale === 1 ? '맞춤' : `${Math.round(scale * 100)}%`}
              </ZoomLabel>
              <Tooltip label="확대">
                <ControlBtn aria-label="확대" onClick={handleZoomIn} disabled={zoomIdx === ZOOM_LEVELS.length - 1}>
                  <ZoomIn size={16} />
                </ControlBtn>
              </Tooltip>
              <Tooltip label={ext === 'pdf' ? '페이지 맞춤' : '원래 크기'}>
                <ControlBtn aria-label={ext === 'pdf' ? '페이지 맞춤' : '원래 크기'} onClick={handleZoomReset}>
                  <RotateCcw size={14} />
                </ControlBtn>
              </Tooltip>
              <Divider />
            </>
          )}
          {isHtml && (
            <Tooltip label={htmlPreview ? '코드 보기' : '미리보기'} position="bottom">
              <ControlBtn
                onClick={() => setHtmlPreview((v) => !v)}
                aria-label={htmlPreview ? 'HTML 코드 보기' : 'HTML 미리보기'}
              >
                {htmlPreview ? <Code2 size={16} /> : <Eye size={16} />}
              </ControlBtn>
            </Tooltip>
          )}
          {headerRight}
          {onToggleFullscreen && (
            <Tooltip label={fullscreen ? '분할 보기' : '전체 화면'} position="bottom">
              <ControlBtn onClick={onToggleFullscreen}>
                {fullscreen ? <Shrink size={16} /> : <Maximize2 size={16} />}
              </ControlBtn>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip label="렌더링 닫기" position="bottom" align="end">
              <ControlBtn onClick={onClose}>
                <X size={16} />
              </ControlBtn>
            </Tooltip>
          )}
        </Controls>
      </Header>

      <Content $noScroll={ext === 'pdf' || isDocx || isPptx || isMarkdown || isImage || isVideo || isCode}>
        {loading && !error && (
          <SkeletonOverlay>
            <FilePreviewSkeleton />
          </SkeletonOverlay>
        )}
        {error ? (
          <ErrorContainer>
            <AlertTriangle size={24} />
            <ErrorText>{error}</ErrorText>
          </ErrorContainer>
        ) : (
          <Suspense fallback={null}>
            {renderContent()}
          </Suspense>
        )}
      </Content>
    </Panel>
  );
};
